import { Stack } from "@mui/system";
import { useMemo, useState } from "react";
import { useNestedDropdown } from "../../../../../hooks";
import { DropdownNestedItem } from "../../../../../types/genereal";
import { DropdownSubItem } from "../../../../ui/dropdown/DropdownSubItem";
import { ChipsList } from "./ChipsList";
import { DropdownParentItem } from "./DropdownParentItem";
import { Divider } from "@mui/material";
import { CustomChip } from "../../../../ui";
import { DropdownInput } from "../../../../ui/dropdown";

const ROW_LENGTH = 4;

type Props = {
  items: DropdownNestedItem[];
  selected: DropdownNestedItem[];
  setSelected: React.Dispatch<React.SetStateAction<DropdownNestedItem[]>>;
  placeholder: string;
  selectAllText?: string;
};

export const NestedDropdown = ({
  items,
  selected,
  setSelected,
  placeholder,
  selectAllText = "Select all",
}: Props) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");

  const {
    nestedItems,
    selectedParents,
    removeParent,
    toggleParent,
    toggleChild,
    removeChild,
    isChildSelected,
    checkParent,
    setInternalSelected,
    internalSelected,
  } = useNestedDropdown({
    items,
    selected,
    setSelected,
  });

  const cancelHandler = () => {
    setIsOpen(false);
    setSearch("");
    setInternalSelected(selected);
  };

  const submitHandler = () => {
    setIsOpen(false);
    setSearch("");
    setSelected(internalSelected);
  };

  const totalSelected = useMemo(() => {
    return selected.reduce((acc, cur) => {
      if (cur.selected) {
        const selected = items.find((elem) => elem.name === cur.name);
        const total = selected ? selected.nestedItems.length : 0;
        return acc + total;
      }
      return acc + cur.nestedItems.length;
    }, 0);
  }, [selected, items]);

  return (
    <Stack gap="8px">
      <DropdownInput
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        search={search}
        setSearch={setSearch}
        placeholder={placeholder}
        selectedCount={totalSelected}
        submitHandler={submitHandler}
        cancelHandler={cancelHandler}
        closeHandler={cancelHandler}
        fullwidth
        showCounter
      >
        {items
          .filter(
            (item) =>
              item.name.toLowerCase().includes(search.toLowerCase()) ||
              item.nestedItems.find((sub) =>
                sub.name.toLowerCase().includes(search.toLocaleLowerCase()),
              ),
          )
          .map((item) => {
            const { isSelected, nestedSelected } = checkParent(item.name);

            return (
              <DropdownParentItem
                initOpen={!!search}
                key={item.name}
                item={item}
                selectedChildCount={nestedSelected}
              >
                <DropdownSubItem
                  title={selectAllText}
                  checked={isSelected}
                  onClick={() => toggleParent(item)}
                />

                {item.nestedItems.length > 0 && (
                  <Divider style={{ margin: "0 16px" }} />
                )}

                {item.nestedItems
                  .filter(({ name }) =>
                    name.toLowerCase().includes(search.toLowerCase()),
                  )
                  .map((sub) => (
                    <DropdownSubItem
                      key={sub.name}
                      title={sub.name}
                      disabled={isSelected}
                      checked={isChildSelected(sub.name)}
                      onClick={() => toggleChild(item.name, sub.name)}
                    />
                  ))}
              </DropdownParentItem>
            );
          })}
      </DropdownInput>

      <ChipsList
        items={selectedParents}
        rowLength={ROW_LENGTH}
        renderItem={({ name, nestedTotal }) => {
          const added = selected.some((el) => name === el.name && el.isAdded);
          return (
            <CustomChip
              key={name}
              label={nestedTotal > 0 ? `${name} (${nestedTotal})` : name}
              onDelete={() => removeParent(name)}
              sx={{
                backgroundColor: added ? "transparent" : "#E8ECF0",
                border: added ? "1px solid #D2D8E2" : "none",
              }}
            />
          );
        }}
      />

      <ChipsList
        items={nestedItems}
        rowLength={ROW_LENGTH}
        buttonText={`+ ${nestedItems.length - ROW_LENGTH} more...`}
        renderItem={({ name, isAdded, parent }) => (
          <CustomChip
            key={name + parent}
            label={name}
            onDelete={() => removeChild(name, parent)}
            sx={{
              backgroundColor: isAdded ? "transparent" : "#E8ECF0",
              border: isAdded ? "1px solid #D2D8E2" : "none",
            }}
          />
        )}
      />
    </Stack>
  );
};
